import React from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { UserLink } from '@/components/UserLink';

import { UserAvatar } from './UserAvatar';
import type { Identity, IdentityLinkMode } from './types';

/** Defines the rich row-style identity presentation options. */
export interface IdentityRowProps {
  /** Supplies the canonical identity data to render. */
  identity: Identity;
  /** Controls whether the row may render as a profile link. */
  linkMode?: IdentityLinkMode;
  /** Sets the rendered avatar size. */
  avatarSize?: number;
  /** Shows the fullname beneath the username when available. */
  showFullname?: boolean;
}

/** Renders avatar, username, and optional fullname in a horizontal row. */
export const IdentityRow: React.FC<IdentityRowProps> = ({
  identity,
  linkMode = 'link',
  avatarSize = 32,
  showFullname = false,
}) => {
  const content = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
      <UserAvatar username={identity.username} avatarUrl={identity.avatarUrl} size={avatarSize} />
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="div"
          sx={{
            fontWeight: 500,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {identity.username}
        </Typography>
        {showFullname && identity.fullname ? (
          <Typography variant="body2" color="text.secondary">
            {identity.fullname}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );

  return linkMode === 'link' ? (
    <UserLink username={identity.username}>{content}</UserLink>
  ) : (
    content
  );
};
